import React, { useState, useRef } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { uid } from "../../lib/format";
import StructuralBackButton from "../shared/StructuralBackButton";
import { CustomIcon } from "../shared/CustomIcon";
import { kcFetch, API_BASE, authHeaders } from "../../lib/apiClient";

async function apiBulkImportMembers(list) {
  const res = await kcFetch(`${API_BASE}/members/bulk-import`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ members: list }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Bulk import failed");
  }
  return res.json();
}

// Canonical member fields the importer can populate.
const TARGET_FIELDS = [
  { key: "firstName",    label: "First Name *", required: true,  aliases: ["first", "firstname", "first name", "given name"] },
  { key: "lastName",     label: "Last Name *",  required: true,  aliases: ["last", "lastname", "last name", "surname", "family name"] },
  { key: "dob",          label: "DOB",          aliases: ["dob", "date of birth", "birthday", "birthdate"] },
  { key: "gender",       label: "Gender",       aliases: ["gender", "sex"] },
  { key: "maritalStatus",label: "Marital Status", aliases: ["marital", "marital status", "marriage"] },
  { key: "phone",        label: "Phone",        aliases: ["phone", "mobile", "cell", "telephone"] },
  { key: "email",        label: "Email",        aliases: ["email", "e-mail", "mail"] },
  { key: "address",      label: "Address",      aliases: ["address", "addr", "street"] },
  { key: "language",     label: "Language",     aliases: ["language", "lang"] },
  { key: "memberStatus", label: "Status",       aliases: ["status", "member status", "memberstatus"] },
  { key: "joinedDate",   label: "Joined Date",  aliases: ["joined", "joined date", "join date", "since"] },
  { key: "departments",  label: "Departments",  aliases: ["dept", "department", "departments", "ministries"] },
];

function autoMap(headers) {
  const map = {};
  TARGET_FIELDS.forEach(f => {
    const h = headers.find(h => f.aliases.some(a => a.toLowerCase() === String(h || "").trim().toLowerCase()));
    if (h) map[f.key] = h;
  });
  return map;
}

export default function BulkImportPage({ currentUser, members, setMembers, addAudit, addNotification, persist }) {
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const onPick = async (e) => {
    setError(""); setSummary(null);
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const ext = f.name.split(".").pop().toLowerCase();
      if (ext === "csv") {
        Papa.parse(f, {
          header: true, skipEmptyLines: true,
          complete: (res) => {
            const rows = res.data || [];
            const heads = res.meta?.fields || [];
            applyParsed(heads, rows);
            setBusy(false);
          },
          error: (err) => { setError(err.message || "CSV parse failed."); setBusy(false); },
        });
      } else if (ext === "xlsx" || ext === "xls") {
        const buf = await f.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
        const heads = json.length ? Object.keys(json[0]) : [];
        applyParsed(heads, json);
        setBusy(false);
      } else {
        setError("Unsupported file type. Use .csv, .xlsx, or .xls.");
        setBusy(false);
      }
    } catch (ex) {
      setError(ex.message || "Could not read the file.");
      setBusy(false);
    }
  };

  const applyParsed = (heads, rows) => {
    if (!rows || rows.length === 0) { setError("File contains no data rows."); return; }
    setRawHeaders(heads);
    setRawRows(rows);
    setMapping(autoMap(heads));
  };

  const previewRows = rawRows.slice(0, 10).map(r => {
    const obj = {};
    TARGET_FIELDS.forEach(f => { obj[f.key] = mapping[f.key] ? (r[mapping[f.key]] || "") : ""; });
    return obj;
  });

  const importNow = async () => {
    if (!mapping.firstName || !mapping.lastName) {
      setError("Map both First Name and Last Name columns before importing.");
      return;
    }
    const errors = [];
    const newMembers = [];
    rawRows.forEach((r, idx) => {
      const fn = String(r[mapping.firstName] || "").trim();
      const ln = String(r[mapping.lastName]  || "").trim();
      if (!fn || !ln) { errors.push({ row: idx + 2, reason: "Missing first or last name" }); return; }
      const get = (k) => mapping[k] ? String(r[mapping[k]] || "").trim() : "";
      newMembers.push({
        id: uid(),
        firstName: fn, lastName: ln,
        dob: get("dob"), gender: get("gender") || "Male",
        maritalStatus: get("maritalStatus") || "Single",
        phone: get("phone") ? [{ label: "Mobile", number: get("phone") }] : [],
        email: get("email") ? [{ label: "Personal", address: get("email") }] : [],
        address: get("address"), language: get("language") || "English",
        memberStatus: get("memberStatus") || "New Member",
        joinedDate: get("joinedDate"),
        departments: get("departments") ? get("departments").split(/[;,]/).map(d => d.trim()).filter(Boolean) : [],
        baptized: false, spiritualBackground: "New to faith",
        additionType: "Joined by decision", additionDate: get("joinedDate"),
        tags: [], familyId: null, photo: null,
      });
    });
    const updated = [...members, ...newMembers];
    try {
      const result = await apiBulkImportMembers(newMembers);
      // Server returns counts; use them for the audit summary
      setMembers(updated);
      addAudit(currentUser.id, currentUser.name, "Bulk import", `Imported ${result.added} members (${result.skipped + errors.length} skipped)`);
      addNotification(`${result.added} members imported${(errors.length || result.skipped) ? ` · ${errors.length + result.skipped} skipped` : ""}`, "general");
      setSummary({ created: result.added, failed: errors });
    } catch (err) {
      setError(`Import failed: ${err.message || err}`);
      return;
    }
    setRawHeaders([]); setRawRows([]); setMapping({});
    if (inputRef.current) inputRef.current.value = "";
  };

  const downloadTemplate = () => {
    const template = `firstName,lastName,dob,gender,maritalStatus,phone,email,address,language,memberStatus,joinedDate,departments
John,Doe,1990-01-15,Male,Single,+1 555-0001,john@email.com,123 Main St,English,Active,2024-01-01,Choir
Jane,Smith,1985-06-20,Female,Married,+1 555-0002,jane@email.com,456 Oak Ave,French,New Member,2024-03-15,Youth;Hospitality`;
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "import_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="bulk-import-page">
      <div style={{ marginBottom: 12 }}>
        <StructuralBackButton to="/dashboard" label="← Dashboard" testId="bulk-import-back" />
      </div>

      <div className="card" style={{ padding: 22, marginBottom: 18 }}>
        <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="download-inbox" size={18} /> Bulk Member Import</div>
        <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 16, fontFamily: "Montserrat,sans-serif" }}>
          Upload a <strong>.csv</strong>, <strong>.xlsx</strong> or <strong>.xls</strong> file. We&apos;ll auto-map common column names; you can adjust any mapping before importing.
        </p>
        <div className="flex gap-8 mb-20" style={{ flexWrap: "wrap" }}>
          <input
            ref={inputRef}
            type="file"
            data-testid="bulk-import-file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={onPick}
            disabled={busy}
            style={{ fontFamily: "Montserrat,sans-serif", fontSize: 12, color: "var(--kc-text)" }}
          />
          <button className="btn btn-outline btn-sm" data-testid="bulk-template-btn" onClick={downloadTemplate}>⬇ Template</button>
        </div>
        {busy && <div style={{ fontSize: 13, color: "var(--text3)" }}>Reading file…</div>}
        {error && <div data-testid="bulk-import-error" style={{ background: "var(--red2,#3a1818)", color: "#ff6b6b", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>{error}</div>}
        {summary && (
          <div data-testid="bulk-import-summary" style={{ background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.40)", color: "#4ade80", padding: "12px 16px", borderRadius: 8, fontSize: 13, fontFamily: "Montserrat,sans-serif" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="check" size={14} /> Imported <strong>{summary.created}</strong> member{summary.created === 1 ? "" : "s"}.</span>
            {summary.failed.length > 0 && (
              <div style={{ marginTop: 6, color: "#FFD580" }}>
                {summary.failed.length} row{summary.failed.length === 1 ? "" : "s"} skipped:
                <ul style={{ margin: "4px 0 0 18px" }}>
                  {summary.failed.slice(0, 8).map((f, i) => <li key={i}>Row {f.row} — {f.reason}</li>)}
                  {summary.failed.length > 8 && <li>…and {summary.failed.length - 8} more</li>}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {rawHeaders.length > 0 && (
        <>
          <div className="card" data-testid="bulk-mapping-card" style={{ padding: 16, marginBottom: 14 }}>
            <div className="form-section-title mb-12">Column mapping ({rawRows.length} data rows)</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 10 }}>
              {TARGET_FIELDS.map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: 11, color: f.required ? "var(--kc-accent)" : "var(--kc-sub)", letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: "Montserrat,sans-serif", fontWeight: 700 }}>{f.label}</label>
                  <select
                    data-testid={`bulk-map-${f.key}`}
                    value={mapping[f.key] || ""}
                    onChange={e => setMapping(m => ({ ...m, [f.key]: e.target.value }))}
                  >
                    <option value="">— Not mapped —</option>
                    {rawHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="flex gap-8" style={{ marginTop: 14 }}>
              <button className="btn btn-gold" data-testid="bulk-import-confirm" onClick={importNow} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="check" size={14} /> Import {rawRows.length} rows</button>
              <button className="btn btn-outline" data-testid="bulk-import-cancel" onClick={() => { setRawHeaders([]); setRawRows([]); setMapping({}); setError(""); }}>Cancel</button>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><div className="card-title">Preview — first {previewRows.length} of {rawRows.length}</div></div>
            <div className="table-wrap">
              <table data-testid="bulk-preview-table">
                <thead>
                  <tr>{TARGET_FIELDS.slice(0, 7).map(f => <th key={f.key}>{f.label.replace(" *", "")}</th>)}</tr>
                </thead>
                <tbody>
                  {previewRows.map((r, i) => (
                    <tr key={i}>
                      {TARGET_FIELDS.slice(0, 7).map(f => (
                        <td key={f.key}>{r[f.key] || <span style={{ color: f.required && !r[f.key] ? "#ff6b6b" : "var(--text3)" }}>{f.required ? "Missing" : "—"}</span>}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
